import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Search,
  Eye,
  Check,
  X,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Save,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { supabase } from "../../integrations/supabase/client";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";

interface Hostel {
  id: string;
  name: string;
  type: string;
  hostel_type: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  address_line1: string | null;
  address_line2: string | null;
  total_capacity: number;
  occupied_count: number | null;
  number_of_rooms: number | null;
  room_types: string[] | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  status: string | null;
  created_at: string;
  updated_at: string;
  facilities: string[] | null;
  funding_needed: number | null;
  funding_type: string | null;
  monthly_operational_cost: number | null;
  govt_support: boolean | null;
  year_established: number | null;
}

const facilitiesOptions = [
  "Library",
  "WiFi",
  "RO Water",
  "Mess",
  "CCTV",
  "Study Hall",
  "Sports",
  "Medical Help",
  "Laundry",
  "Generator",
];
const roomTypesOptions = ["Single", "Double", "Triple", "Dormitory"];

export default function Hostels() {
  const [hostels, setHostels] = useState<Hostel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedHostel, setSelectedHostel] = useState<Hostel | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve");
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState<Partial<Hostel>>({});
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);

  useEffect(() => {
    fetchHostels();
  }, []);

  const fetchHostels = async () => {
    try {
      const { data, error } = await supabase
        .from("hostels")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }
      setHostels(data as Hostel[]);
    } catch (error: any) {
      console.error("Error fetching hostels:", error);
      toast.error("Failed to fetch hostels");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedHostel) {
      return;
    }

    setActionLoading(true);
    try {
      const newStatus = actionType === "approve" ? "approved" : "rejected";

      const { error } = await supabase
        .from("hostels")
        .update({ status: newStatus })
        .eq("id", selectedHostel.id);

      if (error) {
        throw error;
      }

      toast.success(
        `Hostel ${actionType === "approve" ? "approved" : "rejected"} successfully`
      );
      setIsActionDialogOpen(false);
      setSelectedHostel(null);
      setActionReason("");
      fetchHostels();
    } catch (error: any) {
      console.error("Error updating hostel:", error);
      toast.error("Failed to update hostel status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!selectedHostel) {
      return;
    }

    setEditLoading(true);
    try {
      const updateData = {
        name: editForm.name ?? selectedHostel.name,
        type: editForm.type ?? selectedHostel.type,
        hostel_type: editForm.hostel_type,
        city: editForm.city,
        state: editForm.state,
        pincode: editForm.pincode,
        address_line1: editForm.address_line1,
        address_line2: editForm.address_line2,
        total_capacity:
          editForm.total_capacity ?? selectedHostel.total_capacity,
        occupied_count: editForm.occupied_count,
        number_of_rooms: editForm.number_of_rooms,
        room_types: selectedRoomTypes.length > 0 ? selectedRoomTypes : null,
        email: editForm.email,
        phone: editForm.phone,
        website: editForm.website,
        status: editForm.status,
        facilities: selectedFacilities.length > 0 ? selectedFacilities : null,
        funding_needed: editForm.funding_needed,
        funding_type: editForm.funding_type,
        monthly_operational_cost: editForm.monthly_operational_cost,
        govt_support: editForm.govt_support,
        year_established: editForm.year_established,
      };

      const { error } = await supabase
        .from("hostels")
        .update(updateData)
        .eq("id", selectedHostel.id);

      if (error) {
        throw error;
      }

      toast.success("Hostel updated successfully");
      setIsEditDialogOpen(false);
      setSelectedHostel(null);
      setEditForm({});
      fetchHostels();
    } catch (error: any) {
      console.error("Error updating hostel:", error);
      toast.error("Failed to update hostel");
    } finally {
      setEditLoading(false);
    }
  };

  const openActionDialog = (hostel: Hostel, type: "approve" | "reject") => {
    setSelectedHostel(hostel);
    setActionType(type);
    setIsActionDialogOpen(true);
  };

  const openViewDialog = (hostel: Hostel) => {
    setSelectedHostel(hostel);
    setIsViewDialogOpen(true);
  };

  const openEditDialog = (hostel: Hostel) => {
    setSelectedHostel(hostel);
    setEditForm(hostel);
    setSelectedFacilities(hostel.facilities ?? []);
    setSelectedRoomTypes(hostel.room_types ?? []);
    setIsEditDialogOpen(true);
  };

  const toggleFacility = (facility: string) => {
    setSelectedFacilities(prev =>
      prev.includes(facility)
        ? prev.filter(f => f !== facility)
        : [...prev, facility]
    );
  };

  const toggleRoomType = (type: string) => {
    setSelectedRoomTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-600 hover:bg-green-700">
            <CheckCircle className="mr-1 h-3 w-3" /> Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" /> Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" /> Pending
          </Badge>
        );
    }
  };

  const filteredHostels = hostels.filter(hostel => {
    const matchesSearch =
      hostel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hostel.city?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false) ||
      (hostel.state?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStatus =
      statusFilter === "all" || hostel.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = hostels.filter(h => h.status === "pending").length;
  const approvedCount = hostels.filter(h => h.status === "approved").length;
  const rejectedCount = hostels.filter(h => h.status === "rejected").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Hostel Management
          </h1>
          <p className="text-muted-foreground">
            Manage hostel registrations and approvals
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Hostels</p>
                <p className="text-2xl font-bold">{hostels.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Pending Approval
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {pendingCount}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {approvedCount}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  {rejectedCount}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search hostels..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Hostels Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Hostels ({filteredHostels.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sr. No</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHostels.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No hostels found
                  </TableCell>
                </TableRow>
              ) : (
                filteredHostels.map((hostel, index) => (
                  <TableRow key={hostel.id} className="hover:bg-accent">
                    <TableCell>{index + 1}</TableCell>
                    <TableCell className="font-medium">{hostel.name}</TableCell>
                    <TableCell className="capitalize">
                      {hostel.type}{" "}
                      {hostel.hostel_type ? `(${hostel.hostel_type})` : ""}
                    </TableCell>
                    <TableCell>
                      {hostel.city ?? "N/A"}, {hostel.state ?? "N/A"}
                    </TableCell>
                    <TableCell>{hostel.total_capacity}</TableCell>
                    <TableCell>{getStatusBadge(hostel.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openViewDialog(hostel)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(hostel)}
                          title="Edit Hostel"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {hostel.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() =>
                                openActionDialog(hostel, "approve")
                              }
                              title="Approve"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openActionDialog(hostel, "reject")}
                              title="Reject"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Hostel Details
            </DialogTitle>
          </DialogHeader>
          {selectedHostel && (
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="capacity">Capacity</TabsTrigger>
                <TabsTrigger value="funding">Funding</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedHostel.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    {getStatusBadge(selectedHostel.status)}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium capitalize">
                      {selectedHostel.type}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Hostel Type</p>
                    <p className="font-medium capitalize">
                      {selectedHostel.hostel_type ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">
                      {selectedHostel.email ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">
                      {selectedHostel.phone ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Website</p>
                    <p className="font-medium">
                      {selectedHostel.website ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Year Established
                    </p>
                    <p className="font-medium">
                      {selectedHostel.year_established ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Registered On
                    </p>
                    <p className="font-medium">
                      {formatDate(selectedHostel.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Last Updated
                    </p>
                    <p className="font-medium">
                      {formatDate(selectedHostel.updated_at)}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="address" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">
                      Address Line 1
                    </p>
                    <p className="font-medium">
                      {selectedHostel.address_line1 ?? "N/A"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">
                      Address Line 2
                    </p>
                    <p className="font-medium">
                      {selectedHostel.address_line2 ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">City</p>
                    <p className="font-medium">
                      {selectedHostel.city ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">State</p>
                    <p className="font-medium">
                      {selectedHostel.state ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pincode</p>
                    <p className="font-medium">
                      {selectedHostel.pincode ?? "N/A"}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="capacity" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Capacity
                    </p>
                    <p className="font-medium">
                      {selectedHostel.total_capacity} students
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Occupied</p>
                    <p className="font-medium">
                      {selectedHostel.occupied_count ?? 0} students
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Number of Rooms
                    </p>
                    <p className="font-medium">
                      {selectedHostel.number_of_rooms ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Vacant</p>
                    <p className="font-medium">
                      {selectedHostel.total_capacity -
                        (selectedHostel.occupied_count ?? 0)}{" "}
                      students
                    </p>
                  </div>
                </div>
                {selectedHostel.room_types &&
                  selectedHostel.room_types.length > 0 && (
                    <div>
                      <p className="mb-2 text-sm text-muted-foreground">
                        Room Types
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedHostel.room_types.map(type => (
                          <Badge key={type} variant="outline">
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                {selectedHostel.facilities &&
                  selectedHostel.facilities.length > 0 && (
                    <div>
                      <p className="mb-2 text-sm text-muted-foreground">
                        Facilities
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedHostel.facilities.map(facility => (
                          <Badge key={facility} variant="secondary">
                            {facility}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
              </TabsContent>

              <TabsContent value="funding" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Funding Needed
                    </p>
                    <p className="font-medium">
                      {selectedHostel.funding_needed
                        ? `₹${selectedHostel.funding_needed.toLocaleString()}`
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Funding Type
                    </p>
                    <p className="font-medium capitalize">
                      {selectedHostel.funding_type ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Monthly Operational Cost
                    </p>
                    <p className="font-medium">
                      {selectedHostel.monthly_operational_cost
                        ? `₹${selectedHostel.monthly_operational_cost.toLocaleString()}`
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Government Support
                    </p>
                    <p className="font-medium">
                      {selectedHostel.govt_support ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="flex gap-2 border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setIsViewDialogOpen(false);
                if (selectedHostel) {
                  openEditDialog(selectedHostel);
                }
              }}
            >
              <Edit className="mr-2 h-4 w-4" /> Edit Hostel
            </Button>
            {selectedHostel?.status === "pending" && (
              <>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    if (selectedHostel) {
                      openActionDialog(selectedHostel, "approve");
                    }
                  }}
                >
                  <Check className="mr-2 h-4 w-4" /> Approve
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setIsViewDialogOpen(false);
                    if (selectedHostel) {
                      openActionDialog(selectedHostel, "reject");
                    }
                  }}
                >
                  <X className="mr-2 h-4 w-4" /> Reject
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Hostel
            </DialogTitle>
            <DialogDescription>
              Update hostel details. All changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="address">Address</TabsTrigger>
              <TabsTrigger value="capacity">Capacity</TabsTrigger>
              <TabsTrigger value="funding">Funding</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hostel Name *</Label>
                  <Input
                    value={editForm.name ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editForm.status ?? "pending"}
                    onValueChange={value =>
                      setEditForm({ ...editForm, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={editForm.type ?? ""}
                    onValueChange={value =>
                      setEditForm({ ...editForm, type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hostel">Hostel</SelectItem>
                      <SelectItem value="gurukul">Gurukul</SelectItem>
                      <SelectItem value="boarding">Boarding School</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hostel Type</Label>
                  <Select
                    value={editForm.hostel_type ?? ""}
                    onValueChange={value =>
                      setEditForm({ ...editForm, hostel_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boys">Boys</SelectItem>
                      <SelectItem value="girls">Girls</SelectItem>
                      <SelectItem value="co-ed">Co-ed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone ?? ""}
                    maxLength={10}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, "");
                      setEditForm({ ...editForm, phone: value });
                    }}
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={editForm.website ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, website: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Year Established</Label>
                  <Input
                    type="number"
                    value={editForm.year_established ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        year_established: parseInt(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="address" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Address Line 1</Label>
                  <Input
                    value={editForm.address_line1 ?? ""}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        address_line1: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Address Line 2</Label>
                  <Input
                    value={editForm.address_line2 ?? ""}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        address_line2: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={editForm.city ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, city: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={editForm.state ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, state: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input
                    value={editForm.pincode ?? ""}
                    maxLength={6}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, "");
                      setEditForm({ ...editForm, pincode: value });
                    }}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="capacity" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Capacity *</Label>
                  <Input
                    type="number"
                    value={editForm.total_capacity ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        total_capacity: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Occupied Count</Label>
                  <Input
                    type="number"
                    value={editForm.occupied_count ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        occupied_count: parseInt(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Number of Rooms</Label>
                  <Input
                    type="number"
                    value={editForm.number_of_rooms ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        number_of_rooms: parseInt(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Room Types</Label>
                <div className="flex flex-wrap gap-3">
                  {roomTypesOptions.map(type => (
                    <div
                      key={type}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${
                        selectedRoomTypes.includes(type)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      }`}
                      onClick={() => toggleRoomType(type)}
                    >
                      <Checkbox checked={selectedRoomTypes.includes(type)} />
                      <span>{type}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Facilities</Label>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  {facilitiesOptions.map(facility => (
                    <div
                      key={facility}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                        selectedFacilities.includes(facility)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      }`}
                      onClick={() => toggleFacility(facility)}
                    >
                      <Checkbox
                        checked={selectedFacilities.includes(facility)}
                      />
                      <span className="text-sm">{facility}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="funding" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Funding Needed (₹)</Label>
                  <Input
                    type="number"
                    value={editForm.funding_needed ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        funding_needed: parseFloat(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Funding Type</Label>
                  <Select
                    value={editForm.funding_type ?? ""}
                    onValueChange={value =>
                      setEditForm({ ...editForm, funding_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="donation">Donation</SelectItem>
                      <SelectItem value="grant">Grant</SelectItem>
                      <SelectItem value="loan">Loan</SelectItem>
                      <SelectItem value="csr">CSR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Monthly Operational Cost (₹)</Label>
                  <Input
                    type="number"
                    value={editForm.monthly_operational_cost ?? ""}
                    min={0}
                    onWheel={e => e.currentTarget.blur()}
                    onKeyDown={e => {
                      if (["e", "E", "-", "+"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        monthly_operational_cost:
                          parseFloat(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Government Support</Label>
                  <Select
                    value={editForm.govt_support ? "yes" : "no"}
                    onValueChange={value =>
                      setEditForm({
                        ...editForm,
                        govt_support: value === "yes",
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={editLoading}>
              <Save className="mr-2 h-4 w-4" />
              {editLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Hostel" : "Reject Hostel"}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to {actionType} &quot;{selectedHostel?.name}
              &quot;?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm text-muted-foreground">
                {actionType === "approve"
                  ? "Approval Notes (Optional)"
                  : "Rejection Reason"}
              </p>
              <Textarea
                value={actionReason}
                onChange={e => setActionReason(e.target.value)}
                placeholder={
                  actionType === "approve"
                    ? "Add any notes..."
                    : "Please provide a reason for rejection..."
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsActionDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={actionLoading}
              className={
                actionType === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : ""
              }
              variant={actionType === "reject" ? "destructive" : "default"}
            >
              {actionLoading
                ? "Processing..."
                : actionType === "approve"
                  ? "Approve"
                  : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
